import { z } from "zod";
import { INDUSTRY_OPTIONS, MAX_NOTIFICATION_EMAILS, TECHNOLOGY_OPTIONS } from "@kansai-ai-hub/types";
export const userRoleEnum = z.enum(["ENTERPRISE", "MEMBER", "ADMIN"]);
/**
 * 文字列 "true"/"false"/"1"/"0" を boolean に変換する preprocess。
 * URL クエリ由来は常に文字列なので、`z.coerce.boolean()` の罠（"false" → true）を避ける。
 */
const queryBool = z.preprocess((v) => {
    if (typeof v === "boolean")
        return v;
    if (typeof v === "string") {
        const s = v.trim().toLowerCase();
        if (s === "true" || s === "1")
            return true;
        if (s === "false" || s === "0")
            return false;
    }
    return v;
}, z.boolean());
/**
 * ユーザー一覧の query 検証（Users / Admin の両方で利用）。
 *
 * - role: 不正値は 400 にする（Prisma P2009 → 500 を返す穴を塞ぐ V-9）
 * - page / pageSize: int に強制 + 上限 100（DoS 回避）
 * - includeDeleted: ADMIN のみが意味を持つ（Service 層で role 検証）
 */
export const userListQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    role: userRoleEnum.optional(),
    // 自社 scope は Service が organizationId で強制する（クライアントからの組織絞り込みは受け付けない）
    includeDeleted: queryBool.optional().default(false),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
/**
 * 企業一覧の query 検証。
 */
export const companyListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
/**
 * 自分の登録情報の更新（全ロール）。
 *
 * **本人が名乗る情報だけを対象にする。**
 * - メールアドレスは含めない。ログイン ID そのもので、変えるなら新アドレスの到達確認と
 *   全セッションの失効まで面倒を見る必要がある。片手間で足すと乗っ取りの導線になる。
 * - ロール・組織も含めない。自分で権限や所属を変えられてはいけない。
 * - 組織の情報もここでは扱わない。組織は複数人で共有する別エンティティなので、
 *   更新経路も別にする（`organizationProfileUpdateSchema` / `PATCH /users/me/organization`）。
 *   組織名だけは引き続き事務局管轄（同名組織の統合・重複防止をアプリ側で保証しているため）。
 */
export const updateMeSchema = z.object({
    name: z.string().trim().min(1, "氏名を入力してください").max(60),
    // 空文字は「未設定に戻す」。サーバー側で null に正規化する。
    department: z.string().trim().max(100),
    contactRole: z.string().trim().max(100),
});
/**
 * 会員（MEMBER）の自己プロフィール更新。得意業界・得意技術のみを編集できる。
 * 会員ホームの「あなたの業界知見が活きる案件」レーンの推薦に使われる。
 */
export const memberProfileUpdateSchema = z.object({
    industries: z.array(z.enum(INDUSTRY_OPTIONS)).max(INDUSTRY_OPTIONS.length),
    technologies: z.array(z.enum(TECHNOLOGY_OPTIONS)).max(TECHNOLOGY_OPTIONS.length),
});
/** ホームページURLの最大長。`createUserSchema` / 利用申込フォームと揃える。 */
const HOMEPAGE_MAX = 500;
/** 組織概要の最大長。`createUserSchema` / 利用申込フォームと揃える。 */
const ORG_DESCRIPTION_MAX = 1_000;
/**
 * 自組織の登録情報（ホームページURL・組織概要）の自己更新（ENTERPRISE / MEMBER）。
 *
 * **空文字を必ず受ける。** 初期値がブランクの組織（事務局が最低限だけ入れて発行した場合や、
 * 申込フォームで任意項目を空のまま送った場合）が大半なので、空から埋められないと機能しない。
 * 逆に「一度入れた値を消して未設定へ戻す」経路も同じ空文字で表す（サーバ側で null に正規化）。
 *
 * URL は `createUserSchema.homepageUrl` と**同じ規則**にする。`.url()` だけだと
 * `javascript:` / `data:` の危険スキームも通るため、http(s) を明示的に要求する。
 * 空文字を union の別枝にせず refine 1 本で見るのは、前後空白だけの入力（"  "）を
 * トリム後の空文字として受けるため（union だと trim 前の生値で literal("") 判定に落ちる）。
 */
export const organizationProfileUpdateSchema = z.object({
    homepageUrl: z
        .string()
        .trim()
        .max(HOMEPAGE_MAX)
        .refine((v) => v === "" ||
        (z.string().url().safeParse(v).success && /^https?:\/\//.test(v)), "http(s) で始まる有効なURLを入力してください"),
    description: z.string().trim().max(ORG_DESCRIPTION_MAX),
    /**
     * 組織の業界（`Organization.industries`）。**省略時は変更しない。**
     *
     * 企業は登録情報画面の「所属組織」セクションでホームページURL・組織概要と一緒に保存する。
     * 会員は得意業界を「得意分野」セクション（`PATCH /users/me/profile`）で編集するため
     * この経路では送らない。必須にすると、会員の所属組織セクションの保存が
     * 業界を空配列で上書きしてしまう。
     */
    industries: z.array(z.enum(INDUSTRY_OPTIONS)).max(INDUSTRY_OPTIONS.length).optional(),
});
/**
 * 通知メール送信先の自己更新（全ロール）。登録情報画面から自分の配信先を編集する。
 * 各要素は「空文字 or 有効なメール」を許容（空欄・重複・大小文字差はサーバ側で正規化・除去）。
 * 実メール件数（空欄を除く）で最大 MAX_NOTIFICATION_EMAILS 件まで。admin 登録の検証と同一規則。
 */
export const notificationEmailsUpdateSchema = z.object({
    emails: z
        .array(z.string().refine((s) => {
        const t = s.trim();
        return t === "" || z.string().email().safeParse(t).success;
    }, "有効なメールアドレスを入力してください"))
        .refine((arr) => arr.filter((s) => s.trim() !== "").length <= MAX_NOTIFICATION_EMAILS, `通知メールは最大${MAX_NOTIFICATION_EMAILS}件までです`),
});
